/**
 * Yardi/RentCafe Tour Data Source
 *
 * Fetches property credentials and available appointment slots from the
 * Repli360 API. Slots are fetched in bulk (30 days) during initialize() and
 * cached, then filtered per-date on demand. One bulk fetch covers every
 * tour type, so switching tour types is instant (no refetch).
 */

import type { TourDataSource, PropertyCredentials } from './types';
import { isoToApiDate, sortTimeSlots } from './date-utils';

const APT_SYNC_DOMAIN = 'https://app.repli360.com/';
const NO_OF_DAYS = 30;

const API_TOUR_TYPE_TO_KEY: Record<string, string> = {
  'Guided Tour': 'guided_tour',
  'Self-Guided Tour': 'self_guided_tour',
  'Live Video Tour': 'live_video_tour',
};

interface AvailableSlot {
  endTime: string;
  propertyId: number;
  slotType: string;
  startTime: string;
  tourType: string[];
}

export class YardiSource implements TourDataSource {
  readonly id = 'yardi' as const;
  readonly leadRouter = 'RENT_CAFE' as const;
  readonly slotsCachedWithDates = false;
  readonly refetchesOnTourTypeChange = false;

  private cachedSlots: Record<string, AvailableSlot[]> = {};
  private cachedDates: string[] = [];
  private cachedTourTypes: string[] = [];
  private inFlightBulkFetch: Promise<void> | null = null;

  async fetchCredentials(siteId: string): Promise<PropertyCredentials | null> {
    try {
      const res = await fetch(`${APT_SYNC_DOMAIN}admin/creadfetch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `site_id=${encodeURIComponent(siteId)}&type=encoded&dniFetch=yes`,
      });
      const text = await res.text();
      const data = JSON.parse(text);
      if (data && Object.keys(data).length > 0) {
        const creds: PropertyCredentials = {
          siteId: data.site_id || '',
          propertyCode: data.propertyCode || '',
          propertyId: data.PropertyId || '',
          bedrooms: data.bedrooms || {},
        };
        if (!creds.propertyId) return null;
        return creds;
      }
    } catch (err) {
      console.error('[YardiSource] Failed to fetch property credentials:', err);
    }
    return null;
  }

  async initialize(creds: PropertyCredentials): Promise<{ tourTypes: string[]; dates: string[] }> {
    await this.fetchAndCacheSlots(creds);
    return { tourTypes: this.cachedTourTypes, dates: this.cachedDates };
  }

  async fetchAvailableDates(creds: PropertyCredentials, _options?: { tourType?: string | null }): Promise<string[]> {
    if (this.cachedDates.length > 0) return this.cachedDates;
    await this.fetchAndCacheSlots(creds);
    return this.cachedDates;
  }

  async fetchTimeSlotsForDate(
    creds: PropertyCredentials,
    isoDate: string,
    tourType: string | null,
  ): Promise<string[]> {
    if (Object.keys(this.cachedSlots).length === 0) {
      await this.fetchAndCacheSlots(creds);
    }

    const apiDate = isoToApiDate(isoDate);

    const slotsArr: AvailableSlot[] =
      this.cachedSlots[creds.propertyCode] ||
      this.cachedSlots[creds.propertyId] ||
      Object.values(this.cachedSlots).flat();

    const apiTourType = tourType
      ? Object.entries(API_TOUR_TYPE_TO_KEY).find(([, v]) => v === tourType)?.[0] ?? null
      : null;

    const times = new Set<string>();
    for (const slot of slotsArr) {
      if (!slot.startTime) continue;
      const [date, time, period] = slot.startTime.split(' ');
      const matchesTour = !apiTourType || slot.tourType?.includes(apiTourType);
      if (date === apiDate && matchesTour) {
        times.add(`${time} ${period}`);
      }
    }

    return sortTimeSlots(Array.from(times));
  }

  private async fetchAndCacheSlots(creds: PropertyCredentials): Promise<void> {
    if (this.inFlightBulkFetch) return this.inFlightBulkFetch;
    this.inFlightBulkFetch = this.doFetchAndCacheSlots(creds).finally(() => {
      this.inFlightBulkFetch = null;
    });
    return this.inFlightBulkFetch;
  }

  private async doFetchAndCacheSlots(creds: PropertyCredentials): Promise<void> {
    try {
      const body = new URLSearchParams();
      body.set('site_id', creds.siteId);
      body.set('PropertyCode', creds.propertyCode);
      body.set('PropertyId', String(Number(creds.propertyId)));
      body.set('today', new Date().toString());
      body.set('noOfDays', String(NO_OF_DAYS));
      body.set('clo', 'no');

      const res = await fetch(`${APT_SYNC_DOMAIN}admin/get_yardi_v2_availableslots`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const result = await res.json();

      if (result.status === 'true' || result.status === true) {
        this.cachedSlots = result.data;
        this.cachedDates = [
          ...new Set(
            Object.values(this.cachedSlots)
              .flat()
              .map((slot: AvailableSlot) => slot.startTime.split(' ')[0]),
          ),
        ];

        const tourTypeSet = new Set<string>();
        Object.values(this.cachedSlots).flat().forEach((slot) => {
          if (!slot.tourType || !Array.isArray(slot.tourType)) return;
          slot.tourType.forEach((apiType) => {
            const key = API_TOUR_TYPE_TO_KEY[apiType];
            if (key) tourTypeSet.add(key);
          });
        });
        this.cachedTourTypes = Array.from(tourTypeSet);
      }
    } catch (err) {
      console.error('[YardiSource] Failed to fetch available slots:', err);
    }
  }
}
